import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sun, Moon, Bell, User, AlertTriangle, Clock, X, LogOut, Settings, Key, ChevronDown } from 'lucide-react';
import { inventoryApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import Modal from './Modal';
import toast from 'react-hot-toast';

interface HeaderProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

interface ExpiryItem {
  inventory: {
    id: string;
    currentQuantity: number;
    currentWeight: number;
  };
  purchase: {
    id: string;
    internalLotNo: string;
    productName: string;
    expiryDate: string;
    state: string;
  };
  coldStorage: {
    name: string;
  } | null;
  brand: {
    name: string;
  } | null;
}

export default function Header({ theme, toggleTheme }: HeaderProps) {
  const { user, logout, updateProfile, changePassword } = useAuth();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  
  const notificationRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // プロフィール編集フォーム
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });

  // パスワード変更フォーム
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // ダッシュボードと同じAPI（7日以内に期限切れ）を使用
  const { data: expiryData } = useQuery({
    queryKey: ['inventory', 'expiry-alert', 7],
    queryFn: () => inventoryApi.getExpiryAlert(7),
    refetchInterval: 60000,
  });

  const expiryItems: ExpiryItem[] = expiryData?.data?.data || [];
  const expiryCount = expiryData?.data?.total || 0;

  // 期限切れかどうかを判定
  const isExpired = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(dateStr);
    return expiryDate < today;
  };

  // 残り日数を計算
  const getDaysUntil = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(dateStr);
    const diffTime = expiryDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // 外部クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // userが変わったらフォームを更新
  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name, email: user.email });
    }
  }, [user]);

  const expiredCount = expiryItems.filter(item => isExpired(item.purchase.expiryDate)).length;
  const expiringSoonCount = expiryItems.filter(item => !isExpired(item.purchase.expiryDate)).length;

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await updateProfile(profileForm);
    if (success) {
      setIsProfileModalOpen(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('新しいパスワードが一致しません');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('パスワードは6文字以上で入力してください');
      return;
    }
    const success = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
    if (success) {
      setIsPasswordModalOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    }
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
    window.location.href = '/login';
  };

  return (
    <>
      <header
        className="h-16 fixed top-0 right-0 left-[200px] z-40 flex items-center justify-between px-6 border-b"
        style={{
          backgroundColor: 'var(--background-card)',
          borderColor: 'var(--border-color)',
        }}
      >
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            食肉在庫管理
          </h2>
        </div>

        <div className="flex items-center gap-3">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--light-gray-tertiary)',
              color: 'var(--text-secondary)',
            }}
            title={theme === 'light' ? 'ダークモードに切り替え' : 'ライトモードに切り替え'}
          >
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
          </button>

          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => setIsNotificationOpen(!isNotificationOpen)}
              className="p-2 rounded-lg transition-colors relative"
              style={{
                backgroundColor: 'var(--light-gray-tertiary)',
                color: 'var(--text-secondary)',
              }}
              title="通知"
            >
              <Bell size={20} />
              {expiryCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full">
                  {expiryCount > 99 ? '99+' : expiryCount}
                </span>
              )}
            </button>

            {/* Notification Popup */}
            {isNotificationOpen && (
              <div
                className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] overflow-hidden rounded-lg shadow-2xl border animate-fadeIn"
                style={{
                  backgroundColor: 'var(--background-card)',
                  borderColor: 'var(--border-color)',
                }}
              >
                <div
                  className="p-3 border-b flex items-center justify-between sticky top-0 z-10"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Bell size={18} style={{ color: 'var(--accent-color)' }} />
                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                      期限アラート
                    </span>
                    {expiryCount > 0 && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: 'var(--accent-color)', color: 'white' }}
                      >
                        {expiryCount}件
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setIsNotificationOpen(false)}
                    className="p-1 rounded hover:bg-gray-100 transition-colors"
                  >
                    <X size={16} style={{ color: 'var(--text-tertiary)' }} />
                  </button>
                </div>

                {expiryCount > 0 && (
                  <div
                    className="px-3 py-2 border-b flex gap-3 text-xs"
                    style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--light-gray-quaternary)' }}
                  >
                    {expiredCount > 0 && (
                      <span className="flex items-center gap-1 text-red-600">
                        <AlertTriangle size={12} />
                        期限切れ: {expiredCount}
                      </span>
                    )}
                    {expiringSoonCount > 0 && (
                      <span className="flex items-center gap-1 text-orange-500">
                        <Clock size={12} />
                        期限間近: {expiringSoonCount}
                      </span>
                    )}
                  </div>
                )}

                <div className="overflow-y-auto max-h-[50vh]">
                  {expiryItems.length === 0 ? (
                    <div className="p-8 text-center">
                      <Bell size={32} className="mx-auto mb-2 opacity-30" style={{ color: 'var(--text-tertiary)' }} />
                      <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                        期限アラートはありません
                      </p>
                    </div>
                  ) : (
                    expiryItems.map((item) => {
                      const expired = isExpired(item.purchase.expiryDate);
                      const daysUntil = getDaysUntil(item.purchase.expiryDate);
                      return (
                        <div
                          key={item.inventory.id}
                          className={`p-3 border-b border-l-4 hover:bg-gray-50 cursor-pointer transition-colors ${expired ? 'border-l-red-500' : 'border-l-orange-400'}`}
                          style={{ borderBottomColor: 'var(--border-color)' }}
                        >
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5">
                              {expired ? (
                                <AlertTriangle size={16} className="text-red-500" />
                              ) : (
                                <Clock size={16} className="text-orange-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span
                                  className="text-xs font-medium"
                                  style={{ color: expired ? '#dc2626' : '#ea580c' }}
                                >
                                  {expired ? '賞味期限切れ' : `あと${daysUntil}日`}
                                </span>
                                <span className="text-xs whitespace-nowrap" style={{ color: 'var(--text-tertiary)' }}>
                                  {item.purchase.expiryDate}
                                </span>
                              </div>
                              <p className="text-sm mt-1 truncate" style={{ color: 'var(--text-primary)' }}>
                                {item.purchase.productName}
                              </p>
                              <div className="flex items-center gap-2 mt-1 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                <span className={`px-1.5 py-0.5 rounded ${item.purchase.state === 'チルド' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                  {item.purchase.state}
                                </span>
                                <span>LOT: {item.purchase.internalLotNo}</span>
                                <span>残: {item.inventory.currentQuantity}ケース</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 ml-3 pl-3 border-l cursor-pointer hover:opacity-80 transition-opacity"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--accent-color)' }}
              >
                <User size={18} className="text-white" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {user?.name || '管理者'}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {user?.email || 'admin@foodcreate.co.jp'}
                </p>
              </div>
              <ChevronDown size={16} style={{ color: 'var(--text-tertiary)' }} />
            </button>

            {/* User Menu Dropdown */}
            {isUserMenuOpen && (
              <div
                className="absolute right-0 top-full mt-2 w-56 rounded-lg shadow-xl border animate-fadeIn overflow-hidden"
                style={{
                  backgroundColor: 'var(--background-card)',
                  borderColor: 'var(--border-color)',
                }}
              >
                <div className="p-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
                  <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                    {user?.name}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {user?.role === 'admin' ? '管理者' : 'ユーザー'}
                  </p>
                </div>

                <div className="py-1">
                  <button
                    onClick={() => { setIsUserMenuOpen(false); setIsProfileModalOpen(true); }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <Settings size={16} style={{ color: 'var(--text-secondary)' }} />
                    プロフィール設定
                  </button>
                  <button
                    onClick={() => { setIsUserMenuOpen(false); setIsPasswordModalOpen(true); }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <Key size={16} style={{ color: 'var(--text-secondary)' }} />
                    パスワード変更
                  </button>
                </div>

                <div className="border-t py-1" style={{ borderColor: 'var(--border-color)' }}>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-red-600"
                  >
                    <LogOut size={16} />
                    ログアウト
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Profile Modal */}
      <Modal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        title="プロフィール設定"
      >
        <form onSubmit={handleProfileSubmit} className="space-y-4">
          <div>
            <label className="label">名前</label>
            <input
              type="text"
              className="input-field"
              value={profileForm.name}
              onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="label">メールアドレス</label>
            <input
              type="email"
              className="input-field"
              value={profileForm.email}
              onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
              required
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setIsProfileModalOpen(false)}
            >
              キャンセル
            </button>
            <button type="submit" className="btn btn-primary">
              保存
            </button>
          </div>
        </form>
      </Modal>

      {/* Password Modal */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        title="パスワード変更"
      >
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="label">現在のパスワード</label>
            <input
              type="password"
              className="input-field"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="label">新しいパスワード</label>
            <input
              type="password"
              className="input-field"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              required
              minLength={6}
            />
          </div>
          <div>
            <label className="label">新しいパスワード（確認）</label>
            <input
              type="password"
              className="input-field"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              required
              minLength={6}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setIsPasswordModalOpen(false)}
            >
              キャンセル
            </button>
            <button type="submit" className="btn btn-primary">
              変更
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
